import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CostCentersService } from '../../../../services/cost-centers.service';
import { CostCenter, ConsolidatedStatistics, CostCenterStatistic } from '../../../../models/CostCenter';
import { TableAction, TableColumn } from '../../../../shared/components/table/table.component';

interface ConsolidatedStatisticsRow extends CostCenterStatistic {
  costCenterLabel: string;
  budgetLabel: string;
  costLabel: string;
  varianceLabel: string;
}

@Component({
  selector: 'app-cost-center-consolidated-statistics',
  templateUrl: './cost-center-consolidated-statistics.component.html',
  styleUrls: ['./cost-center-consolidated-statistics.component.css']
})
export class CostCenterConsolidatedStatisticsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  statistics: ConsolidatedStatistics | null = null;
  detailRows: ConsolidatedStatisticsRow[] = [];
  costCentersForLookup: CostCenter[] = [];
  isLoading = false;
  error: string | null = null;

  tableTitle = 'Detalhamento por centro de custo';

  columns: TableColumn[] = [
    { titulo: 'Centro de custo', label: 'costCenterLabel' },
    {
      titulo: 'Orçamento',
      label: 'budgetLabel',
      customClass: () => 'text-end'
    },
    {
      titulo: 'Custo',
      label: 'costLabel',
      customClass: () => 'text-end'
    },
    {
      titulo: 'Variação',
      label: 'varianceLabel',
      customClass: (_value, row: ConsolidatedStatisticsRow) => this.varianceCellClass(row)
    },
    {
      titulo: 'Status',
      label: 'status',
      customClass: (_value, row: ConsolidatedStatisticsRow) => this.statusCellClass(row.status)
    }
  ];

  actions: TableAction[] = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view',
      visible: true
    }
  ];

  constructor(
    private costCentersService: CostCentersService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadStatistics();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStatistics(): void {
    this.isLoading = true;
    this.error = null;
    this.statistics = null;

    this.costCentersService
      .getActive({ page: 1, pageSize: 500 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const items = result.items || [];
          this.costCentersForLookup = items;
          const rootIds = this.costCentersService.resolveRootCostCenterIds(items);

          if (rootIds.length === 0) {
            this.isLoading = false;
            this.statistics = {
              totalCostCenters: 0,
              statistics: [],
              summary: { totalBudget: 0, totalCost: 0, overBudgetCount: 0 }
            };
            this.detailRows = [];
            return;
          }

          this.costCentersService
            .getConsolidatedStatistics(rootIds, { suppressErrorToast: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (stats) => {
                this.statistics = stats;
                this.detailRows = this.buildDetailRows(stats);
                this.isLoading = false;
              },
              error: () => {
                this.isLoading = false;
                this.error = 'Não foi possível carregar as estatísticas consolidadas.';
              }
            });
        },
        error: () => {
          this.isLoading = false;
          this.error = 'Não foi possível carregar os centros de custo para o relatório.';
        }
      });
  }

  goBack(): void {
    this.router.navigate(['/financial/cost-centers']);
  }

  viewCostCenterById(costCenterId: number): void {
    const cc = this.costCentersForLookup.find((c) => c.id === costCenterId);
    if (cc?.code) {
      this.router.navigate(['/financial/cost-centers/code', cc.code.trim(), 'view']);
    }
  }

  onRowClick(row: ConsolidatedStatisticsRow): void {
    if (row?.costCenterId) {
      this.viewCostCenterById(row.costCenterId);
    }
  }

  onActionClick(event: { action: string; item: ConsolidatedStatisticsRow }): void {
    if (event.action === 'view' && event.item?.costCenterId) {
      this.viewCostCenterById(event.item.costCenterId);
    }
  }

  private buildDetailRows(stats: ConsolidatedStatistics): ConsolidatedStatisticsRow[] {
    return (stats.statistics || []).map((stat) => ({
      ...stat,
      costCenterLabel: this.getCostCenterName(stat.costCenterId),
      budgetLabel: this.formatCurrency(stat.budget),
      costLabel: this.formatCurrency(stat.cost),
      varianceLabel: `${Number(stat.variance || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    }));
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
  }

  private varianceCellClass(row: ConsolidatedStatisticsRow): string {
    const base = 'text-end';
    if (row.variance > 0) {
      return `${base} text-danger`;
    }
    return `${base} text-success`;
  }

  getCostCenterName(id: number): string {
    const cc = this.costCentersForLookup.find((c) => c.id === id);
    return cc ? `${cc.code} — ${cc.name}` : `Centro #${id}`;
  }

  /** Estilo da célula de status (sem classe `badge` no `<td>` — evita sobrepor a coluna Ações). */
  private statusCellClass(status: string): string {
    const base = 'ccs-stat-status-cell';
    switch ((status || '').trim()) {
      case 'OK':
        return `${base} ccs-stat-status-cell--ok`;
      case 'ATENÇÃO':
        return `${base} ccs-stat-status-cell--warn`;
      case 'CRÍTICO':
        return `${base} ccs-stat-status-cell--crit`;
      default:
        return `${base} ccs-stat-status-cell--muted`;
    }
  }
}
